// src/components/IDCardGenerator.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import "./IDCard.css";

const url = process.env.REACT_APP_BACKEND_URL;

const IDCardGenerator = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [company, setCompany] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const [printType, setPrintType] = useState('');

  // Get absolute URLs for images in public folder
  const getImageUrl = (filename) => {
    return `${window.location.origin}/${filename}`;
  };

  const handlePrint = (type) => {
    if (!selectedEmployee) return;
    
    setPrintType(type);
    setIsPrinting(true);
    
    // Small delay to ensure state update
    setTimeout(() => {
      const printContent = generatePrintContent(type);
      openPrintWindow(printContent, type);
    }, 100);
  };

  const generatePrintContent = (type) => {
    if (!selectedEmployee) return '';

    const frontContent = `
      <div class="id-card-print-front">
        <div class="id-card-frame" style="background-image: url('${getImageUrl('front.jpg')}')">
          <div class="overlay name">${selectedEmployee.fullname || ''}</div>
          <div class="overlay title">${getEmployeeTypeName(selectedEmployee.employee_type_id)}</div>
          <div class="overlay department">${getDepartmentName(selectedEmployee.faculty_id)}</div>
          <div class="overlay issue">${formatDate(selectedEmployee.joined_date)}</div>
          <div class="overlay expire">${formatDate(selectedEmployee.obtained_date)}</div>
          <div class="overlay idno">${selectedEmployee.employee_id || ''}</div>
          <div class="overlay photo">
            ${selectedEmployee.photo ? 
              `<img src="${url}/uploads/${selectedEmployee.photo}" alt="Employee" />` : 
              '<div class="photo-placeholder">Photo</div>'
            }
          </div>
        </div>
      </div>
    `;

    const backContent = `
      <div class="id-card-print-back">
        <div class="id-card-frame" style="background-image: url('${getImageUrl('back.jpg')}')">
          <div class="overlay qr">
            <div class="qr-code">
              <!-- QR code will be generated by the script below -->
            </div>
          </div>
          ${company?.companyseal ? 
            `<div class="overlay seal">
              <img src="${url}/uploads/${company.companyseal}" alt="Company Seal" />
            </div>` : ''
          }
          ${company?.companysignature ? 
            `<div class="overlay signature">
              <img src="${url}/uploads/${company.companysignature}" alt="Company Signature" />
            </div>` : ''
          }
        </div>
      </div>
    `;

    switch (type) {
      case 'front':
        return frontContent;
      case 'back':
        return backContent;
      case 'both':
        return frontContent + backContent;
      default:
        return '';
    }
  };

  const openPrintWindow = (content, type) => {
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
      alert('Please allow popups for printing');
      setIsPrinting(false);
      return;
    }

    const qrData = JSON.stringify({
      employee_id: selectedEmployee.employee_id,
      fullname: selectedEmployee.fullname,
      email: selectedEmployee.email,
      phone: selectedEmployee.phone,
      position: getEmployeeTypeName(selectedEmployee.employee_type_id),
    });

    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${selectedEmployee.fullname} - ID Card ${type}</title>
          <style>
            ${getPrintStyles()}
          </style>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js"></script>
        </head>
        <body>
          <div class="print-container">
            ${content}
          </div>
          <script>
            // Generate QR code using qrcode-generator library
            function generateQRCode(text, container) {
              try {
                const typeNumber = 0; // Auto detect type
                const errorCorrectionLevel = 'L';
                const qr = qrcode(typeNumber, errorCorrectionLevel);
                qr.addData(text);
                qr.make();
                
                const qrSize = 120;
                const cellSize = Math.floor(qrSize / qr.getModuleCount());
                const margin = 0;
                const size = qr.getModuleCount() * cellSize + margin * 2;
                
                let svg = '<svg width="' + qrSize + '" height="' + qrSize + '" viewBox="0 0 ' + size + ' ' + size + '">';
                svg += '<rect width="100%" height="100%" fill="white"/>';
                
                for (let row = 0; row < qr.getModuleCount(); row++) {
                  for (let col = 0; col < qr.getModuleCount(); col++) {
                    if (qr.isDark(row, col)) {
                      svg += '<rect x="' + (col * cellSize + margin) + '" y="' + (row * cellSize + margin) + '" width="' + cellSize + '" height="' + cellSize + '" fill="black"/>';
                    }
                  }
                }
                
                svg += '</svg>';
                container.innerHTML = svg;
              } catch (e) {
                console.error('QR Code generation failed:', e);
                // Fallback: create a simple text representation
                container.innerHTML = '<div style="width:120px;height:120px;background:#f0f0f0;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;font-size:10px;text-align:center;padding:5px;">QR: ' + ${JSON.stringify(selectedEmployee.employee_id)} + '</div>';
              }
            }

            // Generate QR codes when document loads
            document.addEventListener('DOMContentLoaded', function() {
              const qrElements = document.querySelectorAll('.qr-code');
              if (qrElements.length > 0) {
                const qrText = "Employee: ${selectedEmployee.fullname}\\nID: ${selectedEmployee.employee_id}\\nDept: ${getDepartmentName(selectedEmployee.faculty_id)}\\nPosition: ${getEmployeeTypeName(selectedEmployee.employee_type_id)}";
                qrElements.forEach(element => {
                  generateQRCode(qrText, element);
                });
              }

              // Auto-print and close
              setTimeout(() => {
                window.print();
                setTimeout(() => {
                  window.close();
                }, 500);
              }, 100);
            });

            // Fallback in case print is cancelled
            window.onafterprint = function() {
              setTimeout(() => {
                window.close();
              }, 1000);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printDocument);
    printWindow.document.close();
    
    printWindow.onbeforeunload = () => {
      setIsPrinting(false);
    };
  };

  const getPrintStyles = () => `
    @media print {
      @page {
        margin: 0;
        size: 1012px 638px;
      }
      
      body, html {
        margin: 0 !important;
        padding: 0 !important;
        width: 1012px !important;
        height: 638px !important;
      }
    }
    
    body {
      margin: 0;
      padding: 0;
      background: white;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      font-family: Arial, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .print-container {
      display: flex;
      flex-direction: column;
      gap: 0;
    }
    
    .id-card-print-front,
    .id-card-print-back {
      width: 1012px;
      height: 638px;
      page-break-after: always;
      position: relative;
    }
    
    .id-card-frame {
      width: 1012px !important;
      height: 638px !important;
      background-size: 1012px 638px !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
      position: relative !important;
      background-color: white;
    }
    
    .overlay {
      position: absolute;
      color: black;
      font-weight: bold;
      margin: 0;
      padding: 0;
    }
    
    .name { 
      top: 270px; 
      left: 230px; 
      font-size: 28px; 
      font-weight: bold;
    }
    
    .title { 
      top: 375px; 
      left: 260px; 
      font-size: 22px; 
    }
    
    .department { 
      top: 480px; 
      left: 275px; 
      font-size: 24px; 
    }
    
    .issue { 
      top: 600px; 
      left: 150px; 
      font-size: 18px; 
    }
    
    .expire { 
      top: 600px; 
      left: 425px; 
      font-size: 18px; 
    }
    
    .idno { 
      bottom: 75px; 
      right: 30px; 
      font-size: 20px; 
      color: blue; 
    }
    
    .photo {
      position: absolute;
      top: 295px;
      right: 20px;
      width: 220px;
      height: 200px;
      overflow: hidden;
      background: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #ddd;
    }
    
    .photo img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .photo-placeholder {
      color: #999;
      font-size: 14px;
    }
    
    .qr { 
      position: absolute; 
      top: 210px; 
      left: 80px; 
      background: white;
      padding: 10px;
      border: 1px solid #ddd;
    }
    
    .qr-code {
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
    }
    
    .qr-code svg {
      width: 100%;
      height: 100%;
    }
    
    .seal {
      position: absolute;
      top: 210px;
      right: 400px;
      width: 120px;
      height: 120px;
      background: white;
      padding: 5px;
      border: 1px solid #ddd;
    }
    
    .seal img { 
      width: 100%; 
      height: 100%; 
      object-fit: contain; 
    }
    
    .signature {
      position: absolute;
      top: 230px;
      right: 60px;
      width: 120px;
      height: 80px;
      background: white;
      padding: 5px;
      border: 1px solid #ddd;
    }
    
    .signature img { 
      width: 100%; 
      height: 100%; 
      object-fit: contain; 
    }
  `;

  useEffect(() => {
    fetchEmployees();
    fetchDepartmentsAndTypes();
    fetchCompany();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/employee`);
      setEmployees(res.data.data || res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch employees.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentsAndTypes = async () => {
    try {
      const [deptRes, typeRes] = await Promise.all([
        axios.get(`${url}/api/department`),
        axios.get(`${url}/api/employeetype`),
      ]);
      setDepartments(deptRes.data?.data || deptRes.data || []);
      setEmployeeTypes(typeRes.data?.data || typeRes.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${url}/api/company/latest`);
      setCompany(res.data?.data || null);
    } catch (err) {
      console.error("Failed to fetch company:", err);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getEmployeeTypeName = (id) =>
    employeeTypes.find((t) => t._id === id)?.name || "";

  const getDepartmentName = (id) =>
    departments.find((d) => d._id === id)?.name || "";

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="id-card-generator">
      <h1>Employee ID Card Generator</h1>

      {/* Employee Selector */}
      <div className="employee-selector">
        <label htmlFor="employee-select">Select Employee: </label>
        <select
          id="employee-select"
          value={selectedEmployee?._id || ""}
          onChange={(e) =>
            setSelectedEmployee(employees.find((emp) => emp._id === e.target.value) || null)
          }
        >
          <option value="">-- Select an employee --</option>
          {employees.map((emp) => (
            <option key={emp._id} value={emp._id}>
              {emp.fullname} ({emp.employee_id})
            </option>
          ))}
        </select>
      </div>

      {/* Preview Section */}
      {selectedEmployee && (
        <div className="preview-section">
          <h2>Preview for {selectedEmployee.fullname}</h2>
          
          {/* Preview Cards */}
          <div className="cards-container">
            <div className="id-card-front">
              <div className="id-card-frame" style={{ backgroundImage: `url(${getImageUrl('front.jpg')})` }}>
                <div className="overlay name">{selectedEmployee.fullname}</div>
                <div className="overlay title">{getEmployeeTypeName(selectedEmployee.employee_type_id)}</div>
                <div className="overlay department">{getDepartmentName(selectedEmployee.faculty_id)}</div>
                <div className="overlay issue">{formatDate(selectedEmployee.joined_date)}</div>
                <div className="overlay expire">{formatDate(selectedEmployee.obtained_date)}</div>
                <div className="overlay idno">{selectedEmployee.employee_id}</div>
                <div className="overlay photo">
                  {selectedEmployee.photo ? (
                    <img src={`${url}/uploads/${selectedEmployee.photo}`} alt="Employee" />
                  ) : (
                    <div className="photo-placeholder">Photo</div>
                  )}
                </div>
              </div>
            </div>

            <div className="id-card-back">
              <div className="id-card-frame" style={{ backgroundImage: `url(${getImageUrl('back.jpg')})` }}>
                <div className="overlay qr">
                  <QRCodeCanvas
                    value={JSON.stringify({
                      employee_id: selectedEmployee.employee_id,
                      fullname: selectedEmployee.fullname,
                      email: selectedEmployee.email,
                      phone: selectedEmployee.phone,
                      position: getEmployeeTypeName(selectedEmployee.employee_type_id),
                    })}
                    size={120}
                  />
                </div>
                {company?.companyseal && (
                  <div className="overlay seal">
                    <img src={`${url}/uploads/${company.companyseal}`} alt="Company Seal" />
                  </div>
                )}
                {company?.companysignature && (
                  <div className="overlay signature">
                    <img src={`${url}/uploads/${company.companysignature}`} alt="Company Signature" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Print Buttons */}
          <div className="button-row">
            <button 
              onClick={() => handlePrint('front')} 
              disabled={!selectedEmployee || isPrinting}
            >
              {isPrinting && printType === 'front' ? 'Printing...' : 'Print Front'}
            </button>
            <button 
              onClick={() => handlePrint('back')} 
              disabled={!selectedEmployee || isPrinting}
            >
              {isPrinting && printType === 'back' ? 'Printing...' : 'Print Back'}
            </button>
            {/* <button 
              onClick={() => handlePrint('both')} 
              disabled={!selectedEmployee || isPrinting}
            >
              {isPrinting && printType === 'both' ? 'Printing...' : 'Print Both Sides'}
            </button> */}
          </div>
        </div>
      )}

      {!selectedEmployee && (
        <div className="no-selection">
          <p>Please select an employee to generate ID card</p>
        </div>
      )}
    </div>
  );
};

export default IDCardGenerator;